import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BrainCircuit, Sparkles, Camera, Trash2, ChevronRight, Car, FileDown, ArrowLeft } from "lucide-react";

export default function Diagnostico() {
  const [vehicles, setVehicles] = useState([]);
  const [past, setPast] = useState([]);
  const [showList, setShowList] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    year: "", make: "", model: "", engine: "", vin: "", mileage: "",
    symptoms: "",
    obd_codes: "",
    parts_replaced: "",
    service_history: "",
    image_urls: [],
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data));
    api.get("/diagnostics").then(({ data }) => setPast(data));
  }, []);

  const chooseVehicle = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    if (!v) return setForm((f) => ({ ...f, vehicle_id: "" }));
    setForm((f) => ({
      ...f,
      vehicle_id: vid,
      year: v.year || "", make: v.make || "", model: v.model || "",
      engine: v.engine || "", vin: v.vin || "", mileage: v.mileage || "",
    }));
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}${token ? `?auth=${token}` : ""}`;
      setForm((f) => ({ ...f, image_urls: [...f.image_urls, url] }));
      toast.success("Foto adicionada");
    } catch { toast.error("Erro no upload"); }
    finally { setUploading(false); }
    e.target.value = "";
  };

  const removeImage = (i) => setForm((f) => ({ ...f, image_urls: f.image_urls.filter((_, idx) => idx !== i) }));

  const analyze = async () => {
    if (!form.symptoms.trim()) { toast.error("Descreva os sintomas antes de analisar"); return; }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        obd_codes: form.obd_codes
          .split(/[,\s]+/).map((c) => c.trim().toUpperCase()).filter(Boolean),
      };
      if (!payload.vehicle_id) delete payload.vehicle_id;
      const { data } = await api.post("/diagnostics", payload);
      setResult(data);
      toast.success("Análise concluída");
      api.get("/diagnostics").then(({ data }) => setPast(data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao analisar");
    } finally { setLoading(false); }
  };

  const openPdf = (id) => {
    const token = localStorage.getItem("aab_token");
    window.open(`${API}/diagnostics/${id}/pdf${token ? `?auth=${token}` : ""}`, "_blank");
  };

  if (showList) return <DiagnosticList past={past} onBack={() => setShowList(false)} onOpen={(id) => { setShowList(false); openDetail(id, setResult, api); }} onPdf={openPdf} />;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="diagnostico-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Assistente técnico</div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
            Diagnóstico IA <Sparkles className="w-5 h-5 text-primary" />
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Powered by Claude Sonnet 4.5 · códigos OBD, sintomas, fotos e histórico → laudo técnico.</p>
        </div>
        <Button variant="outline" onClick={() => setShowList(true)} data-testid="diag-history-btn">Histórico ({past.length})</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left: vehicle */}
        <div className="border border-border rounded-md bg-card p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Veículo</div>
            <div className="font-display font-bold mb-2">Selecionar da frota (opcional)</div>
            <Select value={form.vehicle_id || undefined} onValueChange={chooseVehicle}>
              <SelectTrigger data-testid="diag-vehicle-select"><SelectValue placeholder="Selecionar veículo ou preencher manualmente" /></SelectTrigger>
              <SelectContent>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.year} {v.make} {v.model} · {v.plate || v.vin?.slice(-6)} · {v.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Ano</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
            <div><Label>Marca</Label><Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="diag-make" /></div>
            <div><Label>Modelo</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="diag-model" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Motor</Label><Input value={form.engine} onChange={(e) => setForm({ ...form, engine: e.target.value })} placeholder="Ex: 2.0L I4" /></div>
            <div><Label>Milhagem (Mileage)</Label><Input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} /></div>
          </div>
          <div><Label>VIN</Label><Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="font-mono" /></div>
        </div>

        {/* Right: symptoms */}
        <div className="border border-border rounded-md bg-card p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sintomas *</div>
            <div className="font-display font-bold mb-2">Descrição completa do problema</div>
            <Textarea rows={5} value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })}
              placeholder="Ex: Motor falha na aceleração em rotação média, luz de check engine intermitente, consumo aumentou 20%…"
              data-testid="diag-symptoms" />
          </div>
          <div>
            <Label>Códigos OBD-II lidos</Label>
            <Input value={form.obd_codes} onChange={(e) => setForm({ ...form, obd_codes: e.target.value })}
              placeholder="Ex: P0301, P0171, P0455 (separados por vírgula)"
              data-testid="diag-obd" className="font-mono uppercase" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-border rounded-md bg-card p-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contexto adicional</div>
            <div className="font-display font-bold">Peças recentemente substituídas</div>
          </div>
          <Textarea rows={3} value={form.parts_replaced} onChange={(e) => setForm({ ...form, parts_replaced: e.target.value })}
            placeholder="Ex: Velas trocadas há 15 dias, filtro de combustível há 2 meses…" />
        </div>
        <div className="border border-border rounded-md bg-card p-5 space-y-3">
          <div>
            <div className="font-display font-bold">Histórico de serviços relevante</div>
          </div>
          <Textarea rows={3} value={form.service_history} onChange={(e) => setForm({ ...form, service_history: e.target.value })}
            placeholder="Ex: Revisão completa há 6 meses, correia dentada trocada em 80.000 km…" />
        </div>
      </div>

      {/* Photos */}
      <div className="border border-border rounded-md bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fotos</div>
            <div className="font-display font-bold">Prints do scanner, painel, motor</div>
          </div>
          <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-input hover:bg-accent cursor-pointer text-sm">
            <Camera className="w-4 h-4" />
            {uploading ? "Enviando…" : "Adicionar foto"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} data-testid="diag-photo-input" />
          </label>
        </div>
        {form.image_urls.length === 0 ? <div className="text-sm text-muted-foreground">Nenhuma foto (opcional).</div> :
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {form.image_urls.map((p, idx) => (
              <div key={idx} className="relative group">
                <img src={p} alt="" className="w-full h-24 object-cover rounded border border-border" />
                <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 p-1 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>}
      </div>

      <div className="flex justify-end">
        <Button onClick={analyze} disabled={loading} size="lg" data-testid="diag-analyze-btn" className="min-w-52">
          <BrainCircuit className="w-4 h-4" />
          {loading ? "Analisando com IA…" : "Analisar Diagnóstico"}
        </Button>
      </div>

      {result && <DiagnosticResultCard result={result} onPdf={() => openPdf(result.id)} />}
    </div>
  );
}

function openDetail(id, setResult, api) {
  api.get(`/diagnostics/${id}`).then(({ data }) => setResult(data));
}

function DiagnosticResultCard({ result, onPdf }) {
  const a = result.analysis || {};
  return (
    <div className="border-2 border-primary rounded-md bg-card overflow-hidden animate-in fade-in duration-500" data-testid="diag-result">
      <div className="px-5 py-3 border-b border-border bg-primary/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <div className="font-display font-bold">Análise Concluída</div>
        </div>
        <Button variant="outline" size="sm" onClick={onPdf} data-testid="diag-pdf-btn"><FileDown className="w-3.5 h-3.5" /> PDF</Button>
      </div>
      <div className="p-5 space-y-6">
        {a.resumo && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Resumo executivo</div>
            <p className="text-sm leading-relaxed">{a.resumo}</p>
          </div>
        )}

        {a.possiveis_causas?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Possíveis causas</div>
            <div className="space-y-2">
              {a.possiveis_causas.map((c, i) => (
                <div key={i} className="border border-border rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display font-semibold text-sm">{c.nome}</div>
                    <div className="text-xs font-mono px-2 py-0.5 rounded bg-primary/10 text-primary">{c.probabilidade}%</div>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-2">
                    <div className="h-full bg-primary transition-all" style={{ width: `${c.probabilidade}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{c.descricao}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {a.passos_diagnostico?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Passos de diagnóstico</div>
              <ol className="space-y-1.5 text-sm">
                {a.passos_diagnostico.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-primary font-mono text-xs mt-0.5">{i + 1}.</span>
                    <span className="text-muted-foreground">{s.replace(/^\d+\.\s*/, "")}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="space-y-4">
            {a.ferramentas_necessarias?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Ferramentas</div>
                <div className="flex flex-wrap gap-1.5">
                  {a.ferramentas_necessarias.map((t, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-secondary">{t}</span>
                  ))}
                </div>
              </div>
            )}
            {a.pecas_sugeridas?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Peças sugeridas</div>
                <div className="flex flex-wrap gap-1.5">
                  {a.pecas_sugeridas.map((t, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-primary/10 text-primary">{t}</span>
                  ))}
                </div>
              </div>
            )}
            {a.tempo_estimado_min && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Tempo estimado</div>
                <div className="font-display text-xl font-bold">{a.tempo_estimado_min} min</div>
              </div>
            )}
          </div>
        </div>

        {a.recomendacoes && (
          <div className="border-t border-border pt-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Recomendações finais</div>
            <p className="text-sm whitespace-pre-line text-muted-foreground leading-relaxed">{a.recomendacoes}</p>
          </div>
        )}

        {a.tsbs_recalls?.length > 0 && (
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold">TSBs/Recalls conhecidos:</span> {a.tsbs_recalls.join(" · ")}
          </div>
        )}

        <div className="text-[10px] text-muted-foreground/70 pt-2 border-t border-border">
          Este laudo foi gerado com apoio de inteligência artificial (Claude Sonnet 4.5) e deve ser validado por um técnico habilitado.
        </div>
      </div>
    </div>
  );
}

function DiagnosticList({ past, onBack, onOpen, onPdf }) {
  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="diag-history">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2"><ArrowLeft className="w-4 h-4" /> Voltar</Button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Histórico</div>
        <h2 className="font-display text-2xl font-bold tracking-tight">Diagnósticos anteriores</h2>
      </div>
      {past.length === 0 ? <div className="text-sm text-muted-foreground">Nenhum diagnóstico realizado.</div> :
        <div className="space-y-2">
          {past.map((d) => {
            const v = d.vehicle_snapshot || {};
            const top = d.analysis?.possiveis_causas?.[0];
            return (
              <div key={d.id} className="border border-border rounded-md bg-card p-4 flex items-center gap-4" data-testid={`diag-history-${d.id}`}>
                <div className="w-16 h-12 bg-secondary rounded overflow-hidden shrink-0">
                  {d.vehicle?.photo_url ? <img src={d.vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center"><Car className="w-5 h-5 text-muted-foreground" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-sm">{v.year} {v.make} {v.model}</div>
                  <div className="text-xs text-muted-foreground truncate">{top?.nome || d.input?.symptoms?.slice(0, 80)}</div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">{new Date(d.created_at).toLocaleDateString("pt-BR")}</div>
                <Button variant="outline" size="sm" onClick={() => onPdf(d.id)}><FileDown className="w-3.5 h-3.5" /></Button>
              </div>
            );
          })}
        </div>}
    </div>
  );
}
